import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import { AuthContext } from "../../../../config/AuthContext";
import { Button, Table, TableProps, Card, Typography, Tooltip, Space, Avatar, Progress } from "antd";
import { ColumnsType } from "antd/es/table";
import AvaluacioColors from "../../../../styles/AvaluacioColors";
import { GetProjecteId } from "../../../../dao/abp/GetProjecteId";
import { GetAlumnesValidacioByProject } from "../../../../dao/abp/GetAlumnesValidacioByProject";
import { GetGrupById } from "../../../../dao/abp/GetProjecteIdGrupId";
import { BsEnvelope, BsEye, BsFileExcel, BsFilePdf, BsGoogle } from "react-icons/bs";
import { GetAlumnesTasquesByProject } from "../../../../dao/abp/GetAlumnesTasquesByProject";
import { GetRankingsUsuariId } from "../../../../dao/jutge/GetRankingsUsuariId";

const ProjecteIdResum = () => {
    const { isLoggedIn, userId, user, setBreadCrumb } = useContext(AuthContext);
    const { id_grup, id_projecte, id_usuari } = useParams();

    const navigate = useNavigate();
    const [modules, setModules] = useState<any[]>([]);
    const [grup, setGrups] = useState<any[]>([]);
    const [avatars, setAvatars] = useState<Record<string, string>>({});

    const [projecte, setProjecte] = useState<any>();
    const [valdacio, setValidacio] = useState<any[]>([]);

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isLoggedIn) {
            checkProjecte(id_projecte);
            checkValidacio();
            fetchAvatars();
        }
    }, [id_projecte, id_grup]);

    const fetchAvatars = async () => {
        try {
            const rankings = await GetRankingsUsuariId(userId);
            if (rankings && Array.isArray(rankings)) {
                const map: Record<string, string> = {};
                rankings.forEach((r: any) => {
                    if (r.id_usuari && r.avatar) {
                        map[r.id_usuari] = r.avatar;
                    }
                });
                setAvatars(map);
            }
        } catch (e) {
            console.error("Error fetching avatars", e);
        }
    };

    async function checkProjecte(id: any) {
        setLoading(true);
        const data = await GetProjecteId(id_projecte + "", 0);
        const grupData = await GetGrupById(id_grup + "");
        const tasks = await GetAlumnesTasquesByProject(id_grup + "", id_projecte + "", 0);

        // Calculate progress
        const studentStats: { [key: string]: { total: number, graded: number } } = {};

        if (grupData.alumnes) {
            grupData.alumnes.forEach((stu: any) => {
                const sid = stu.id || stu.id_usuari;
                studentStats[sid] = { total: 0, graded: 0 };
            });
        }

        if (tasks) {
            tasks.forEach((task: any) => {
                if (task.grup) {
                    task.grup.forEach((stu: any) => {
                        const sid = stu.id_usuari;
                        if (!studentStats[sid]) studentStats[sid] = { total: 0, graded: 0 };

                        studentStats[sid].total++;
                        if (parseFloat(stu.nota) !== 0) {
                            studentStats[sid].graded++;
                        }
                    });
                }
            });
        }

        const enrichedStudents = (grupData.alumnes || []).map((stu: any) => {
            const sid = stu.id || stu.id_usuari;
            const stats = studentStats[sid] || { total: 0, graded: 0 };
            const percent = stats.total === 0 ? 0 : (stats.graded / stats.total) * 100;
            return {
                ...stu,
                progress: percent,
                pending: 100 - percent
            };
        });

        setGrups(enrichedStudents);

        setBreadCrumb([{ nom: 'Home', nav: '/home' },
        { nom: 'Avaluació', nav: '/abp' },
        { nom: 'Projectes', nav: '/abp/projectes' },
        { nom: `${data.nom}`, nav: `/abp/projectes/${data.id}` },
        { nom: 'Grups', nav: `/abp/projectes/${data.id}` },
        { nom: `${grupData.alumnes?.map((value: any) => value.nom).join(', ')}`, nav: `/abp/projectes/${data.id}/grups/${id_grup}` },
        { nom: 'Avaluació', nav: `/abp/projectes/${data.id}/grups/${id_grup}/avaluacio` }], true);

        setProjecte(data);
        await setLoading(false);
    }

    async function checkValidacio() {
        setLoading(true);
        const val = await GetAlumnesValidacioByProject(userId + "", id_projecte + "");
        setValidacio(val);
        await setLoading(false);
    }

    const { Title, Text } = Typography;

    const columns: ColumnsType<any> = [
        {
            key: 'avatar',
            title: '',
            width: 60,
            render: (_: any, record: any) => (
                record.nom !== 'TOTS' ? <Avatar src={avatars[record.id] || record.foto || record.avatar_url} style={{ backgroundColor: AvaluacioColors.colors.color1 }}>{record.nom?.[0]}</Avatar> : null
            )
        },
        {
            key: 'id',
            dataIndex: 'id',
            width: 50,
            responsive: ['lg'],
            render: (text, record) => record.nom !== 'TOTS' ? <Text type="secondary">#{text}</Text> : null
        },
        {
            key: 'nom',
            dataIndex: 'nom',
            title: 'Nom',
            width: 150,
            render: (text) => <Text strong>{text}</Text>
        },
        {
            key: 'cognoms',
            dataIndex: 'cognoms',
            title: 'Cognoms',
            width: 150,
            render: (text) => <Text>{text}</Text>
        },
        {
            key: 'progress',
            title: '% Evaluat',
            width: 150,
            render: (_: any, record: any) => record.nom !== 'TOTS' ? (
                <div style={{ width: '100%' }}>
                    <Progress percent={Math.round(record.progress || 0)} size="small" strokeColor="green" />
                </div>
            ) : null
        },
        {
            key: 'pending',
            title: '% Pendent',
            width: 150,
            render: (_: any, record: any) => record.nom !== 'TOTS' ? (
                <div style={{ width: '100%' }}>
                    <Progress percent={Math.round(record.pending || 0)} size="small" strokeColor="orange" />
                </div>
            ) : null
        },
        {
            title: 'Accions',
            key: 'accions',
            align: 'center',
            render: (_: any, record: any) => (
                <Space>
                    <Tooltip title="Descarregar Informe PDF">
                        <Button
                            icon={<BsFilePdf style={{ color: 'red' }} />}
                            href={record.nom === 'TOTS'
                                ? `${process.env.REACT_APP_API_URL}:8061/projectes/${id_projecte}/informe`
                                : `${process.env.REACT_APP_API_URL}:8061/projectes/${id_projecte}/alumnes/${record.id}/informe`}
                            target="_blank"
                            type="text"
                        />
                    </Tooltip>
                    <Tooltip title="Descarregar Notes Excel">
                        <Button
                            icon={<BsFileExcel style={{ color: 'green' }} />}
                            href={record.nom === 'TOTS'
                                ? `${process.env.REACT_APP_API_URL}:8061/projectes/${id_projecte}/grups/${id_grup}/tasques/excel`
                                : `${process.env.REACT_APP_API_URL}:8061/projectes/${id_projecte}/alumnes/${record.id}/tasques/excel`}
                            target="_blank"
                            type="text"
                        />
                    </Tooltip>
                </Space>
            )
        }
    ];

    const tableProps: TableProps<any> = {
        loading
    };

    return (
        <div style={{ padding: '24px', maxWidth: 1200, margin: '0 auto' }}>
            <Card
                title={<Title level={4} style={{ margin: 0 }}>Descarregar Notes</Title>}
                bordered={false}
                style={{ borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}
            >
                <Table
                    showHeader={true}
                    columns={columns}
                    size={"middle"}
                    {...tableProps}
                    dataSource={[
                        {
                            id: '',
                            nom: 'TOTS',
                            cognoms: '',
                            content: ''
                        },
                        ...grup
                    ]}
                    pagination={false}
                    rowKey="id"
                />
            </Card>
        </div>
    );
};

export default ProjecteIdResum;
